import React, { useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { Upload, X } from 'lucide-react';
import { toast } from 'react-toastify';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://store.comparehomeupgrades.com/api';

function findColumn(headerRow, candidates) {
  for (let i = 0; i < headerRow.length; i++) {
    const h = String(headerRow[i] || '').trim().toLowerCase();
    if (candidates.includes(h)) return i;
  }
  return -1;
}

// Upload a "Master SKU" + "Quantity" spreadsheet, update master_products.quantity
// for each row, and push the new quantity live to the requested platform scope
// ('all', or one of shopify/ebay/amazon). Parsing happens client-side (SheetJS);
// the actual updates + live pushes run as a background job on the server, polled
// here, since pushing to many stores can take a while (eBay/Amazon are rate-limited).
export default function ImportQuantitiesButton({ platform, label = 'Import Quantities', onDone, dark = false, stores = [] }) {
  const inputRef = useRef(null);
  // '' = every store of this platform; otherwise only the chosen store gets the pushes
  const [storeId, setStoreId] = useState('');
  const chosenStore = stores.find(s => String(s.id) === String(storeId));
  const [importing, setImporting] = useState(false);
  const [notFoundSkus, setNotFoundSkus] = useState(null); // null = hidden, [] or [...] = show modal
  const [progress, setProgress] = useState('');

  // Saves every problem from the import to an Excel file automatically, so the details
  // (which SKU, which store, which platform, what went wrong) don't vanish with the toast.
  const downloadErrorReport = (s) => {
    const wb = XLSX.utils.book_new();
    const failures = s.failures || [];
    const notFound = s.notFoundSkus || [];

    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
      ['Master SKU', 'Platform', 'Store', 'Platform SKU / Item ID', 'Variation SKU', 'Quantity we tried to set', 'Error'],
      ...failures.map(f => [f.master_sku, f.platform, f.store, f.platform_sku, f.variation_sku, f.quantity, f.error]),
    ]), 'Push failures');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
      ['Master SKU (not found in our mapping)'],
      ...notFound.map(sku => [sku]),
    ]), 'SKUs not found');

    // Same failed SKUs in the exact format the importer reads (Master SKU + Quantity), so the
    // failures can be re-imported as-is once the cause is fixed. One row per SKU.
    const retry = new Map();
    for (const f of failures) if (f.master_sku && !retry.has(f.master_sku)) retry.set(f.master_sku, f.quantity);
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
      ['Master SKU', 'Quantity'],
      ...[...retry.entries()],
    ]), 'Re-import these');

    const stamp = new Date().toISOString().slice(0, 16).replace('T', ' ').replace(':', '-');
    XLSX.writeFile(wb, `Quantity Import Errors ${stamp}.xlsx`);
  };

  const downloadNotFoundList = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      ['Master SKU (not found in our system)'],
      ...notFoundSkus.map(sku => [sku]),
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Not Found');
    XLSX.writeFile(wb, 'Master SKUs Not Found.xlsx');
  };

  const handleFile = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;

    setImporting(true);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });

      let headerIdx = -1, skuCol = -1, qtyCol = -1, sheetName = wb.SheetNames[0];
      for (const name of wb.SheetNames) {
        const rows = XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, defval: '' });
        for (let i = 0; i < rows.length; i++) {
          const s = findColumn(rows[i], ['master sku', 'sku']);
          const q = findColumn(rows[i], ['quantity', 'qty', 'stock']);
          if (s !== -1 && q !== -1) { headerIdx = i; skuCol = s; qtyCol = q; sheetName = name; break; }
        }
        if (headerIdx !== -1) break;
      }

      if (headerIdx === -1) {
        toast.error('Could not find "Master SKU" and "Quantity" columns in that file');
        return;
      }

      const rows = XLSX.utils.sheet_to_json(wb.Sheets[sheetName], { header: 1, defval: '' });
      const parsed = [];
      for (let i = headerIdx + 1; i < rows.length; i++) {
        const sku = String(rows[i][skuCol] || '').trim();
        const qtyRaw = rows[i][qtyCol];
        if (!sku || qtyRaw === '' || qtyRaw === undefined || qtyRaw === null || isNaN(Number(qtyRaw))) continue;
        parsed.push({ master_sku: sku, quantity: Math.round(Number(qtyRaw)) });
      }

      if (parsed.length === 0) {
        toast.error('No valid Master SKU + Quantity rows found in that file');
        return;
      }

      const token = localStorage.getItem('token');
      const startRes = await fetch(`${API_BASE_URL}/sku-mapping/bulk-quantity-update`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ platform, rows: parsed, ...(storeId ? { store_id: Number(storeId) } : {}) }),
      });
      const startData = await startRes.json();
      if (!startData.success) { toast.error(startData.error || 'Import failed to start'); return; }

      toast.info(`Importing ${parsed.length} row${parsed.length !== 1 ? 's' : ''}, updating and pushing to ${
        platform === 'all' ? 'all platforms' : chosenStore ? chosenStore.store_name + ' only' : `all ${platform} stores`
      }…`);

      let done = false;
      while (!done) {
        await new Promise(r => setTimeout(r, 1500));
        const statusRes = await fetch(`${API_BASE_URL}/sku-mapping/bulk-quantity-update/status/${platform}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const statusData = await statusRes.json();
        const s = statusData.status;
        if (s && s.running) {
          setProgress(
            s.phase === 'pushing' ? `Pushing ${s.pushDone}/${s.pushTotal}`
            : s.phase === 'retrying' ? `Retrying ${s.pushFailed} slow…`
            : `Saving ${s.processed}/${s.total}`
          );
        }
        if (!s || !s.running) {
          done = true;
          if (s && s.phase === 'done') {
            const hasProblems = (s.failures && s.failures.length > 0) || (s.notFoundSkus && s.notFoundSkus.length > 0);
            toast.success(
              `Import done: ${s.updated} quantities updated, ${s.pushSuccess} pushed live` +
              `${s.pushFailed ? `, ${s.pushFailed} push failures` : ''}` +
              `${s.notFound ? `, ${s.notFound} SKUs not found` : ''}` +
              `${hasProblems ? ' — error report downloaded' : ''}`,
              { autoClose: 8000 }
            );
            if (hasProblems) downloadErrorReport(s);
            if (s.notFoundSkus && s.notFoundSkus.length > 0) setNotFoundSkus(s.notFoundSkus);
          }
          if (onDone) onDone();
        }
      }
    } catch (err) {
      toast.error('Failed to read/import file');
      console.error(err);
    } finally {
      setImporting(false);
      setProgress('');
    }
  };

  return (
    <>
      <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleFile} />
      {stores.length > 1 && (
        <select
          value={storeId}
          onChange={e => setStoreId(e.target.value)}
          disabled={importing}
          title="Which store(s) the imported quantities are pushed to"
          className={dark
            ? 'px-2 py-2 bg-white/10 text-white text-sm rounded-lg border border-white/20 disabled:opacity-50 [color-scheme:dark]'
            : 'px-3 py-2.5 bg-white border border-gray-200 text-gray-700 text-sm rounded-lg disabled:opacity-50'}
        >
          <option value="">All stores</option>
          {stores.map(s => <option key={s.id} value={s.id}>{s.store_name}</option>)}
        </select>
      )}
      <button
        onClick={() => inputRef.current && inputRef.current.click()}
        disabled={importing}
        className={dark
          ? 'flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/20 text-white text-sm rounded-lg disabled:opacity-50 whitespace-nowrap'
          : 'flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 font-medium disabled:opacity-50 whitespace-nowrap'}
        title="Upload a spreadsheet with Master SKU + Quantity columns to update and push stock in bulk"
      >
        <Upload size={16} className={importing ? 'animate-pulse' : ''} />
        {importing ? (progress || 'Importing…') : label}
      </button>

      {notFoundSkus && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-bold text-gray-800">
                {notFoundSkus.length} Master SKU{notFoundSkus.length !== 1 ? 's' : ''} not found
              </h3>
              <button onClick={() => setNotFoundSkus(null)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Everything else in the file was updated. These SKUs don't exist in our mapping yet, so they were
              skipped — check them against your master sheet and re-upload once corrected.
            </p>
            <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg mb-4">
              {notFoundSkus.map((sku, i) => (
                <div key={i} className={`px-3 py-2 text-sm font-mono text-gray-700 ${i % 2 ? 'bg-gray-50' : ''}`}>{sku}</div>
              ))}
            </div>
            <div className="flex gap-3">
              <button
                onClick={downloadNotFoundList}
                className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium text-sm"
              >
                Download as Excel
              </button>
              <button
                onClick={() => setNotFoundSkus(null)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
